/**
 * EDR Service - Handles all Endpoint Detection & Response API calls
 */

import authenticatedFetch from '../utils/apiClient';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'https://cyberguard-backend-wa00.onrender.com';

export const edrService = {
  /**
   * Get all registered endpoints for the current user
   */
  async getEndpoints() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/endpoints`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch endpoints`);
      }

      const data = await response.json();
      return data.endpoints || [];
    } catch (error) {
      console.error('Error fetching endpoints:', error);
      throw error;
    }
  },

  /**
   * Delete a specific endpoint
   * @param {string} endpoint_id - Endpoint ID to delete
   */
  async deleteEndpoint(endpoint_id) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/endpoints/${endpoint_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to delete endpoint`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting endpoint:', error);
      throw error;
    }
  },

  /**
   * Clear all endpoints for current user
   */
  async clearAllEndpoints() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/endpoints/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to clear endpoints`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error clearing endpoints:', error);
      throw error;
    }
  },

  /**
   * Generate a registration code for easy agent setup
   */
  async generateRegistrationCode() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/registration-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to generate registration code`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating registration code:', error);
      throw error;
    }
  },

  /**
   * Get detected threats
   * @param {Object} options - Filter options
   * @param {string} options.endpoint_id - Filter by endpoint ID
   * @param {string} options.severity - Filter by severity (critical, high, medium, low)
   * @param {number} options.limit - Limit results (default: 100)
   */
  async getThreats(options = {}) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const params = new URLSearchParams();
      if (options.endpoint_id) params.append('endpoint_id', options.endpoint_id);
      if (options.severity) params.append('severity', options.severity);
      if (options.limit) params.append('limit', options.limit);

      const response = await authenticatedFetch(`${API_URL}/api/edr/threats?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch threats`);
      }

      const data = await response.json();
      return data.threats || [];
    } catch (error) {
      console.error('Error fetching threats:', error);
      throw error;
    }
  },

  /**
   * Get overall EDR status
   */
  async getStatus() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch EDR status`);
      }

      const data = await response.json();
      return {
        threat_level: data.threat_level || 'low',
        system_health: data.system_health || {
          cpu: 0,
          memory: 0,
          disk: 0,
          network: 0
        },
        endpoints_monitored: data.endpoints_monitored || 0,
        active_threats: data.active_threats || 0,
        ...data
      };
    } catch (error) {
      console.error('Error fetching EDR status:', error);
      throw error;
    }
  },

  /**
   * Resolve/close a threat
   * @param {string} threat_id - Threat ID to resolve
   * @param {string} resolution_notes - Notes about resolution
   */
  async resolveThreat(threat_id, resolution_notes = '') {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(
        `${API_URL}/api/edr/threats/${threat_id}/resolve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ resolution_notes })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to resolve threat`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resolving threat:', error);
      throw error;
    }
  },

  /**
   * Take action on a threat
   * @param {string} threat_id - Threat ID
   * @param {string} action - Action to take (isolate, quarantine, block, etc.)
   */
  async takeThreatAction(threat_id, action) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(
        `${API_URL}/api/edr/threats/${threat_id}/action`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to execute action`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error executing threat action:', error);
      throw error;
    }
  },

  /**
   * Register endpoint (for EDR agent installation)
   * @param {Object} endpointData - Endpoint information
   */
  async registerEndpoint(endpointData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(endpointData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to register endpoint`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error registering endpoint:', error);
      throw error;
    }
  },

  /**
   * Submit telemetry data from endpoint
   * @param {Object} telemetryData - Telemetry information
   */
  async submitTelemetry(telemetryData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/telemetry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(telemetryData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to submit telemetry`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting telemetry:', error);
      throw error;
    }
  },

  /**
   * Get EDR monitoring settings for current user
   */
  async getSettings() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch EDR settings`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching EDR settings:', error);
      throw error;
    }
  },

  /**
   * Toggle EDR monitoring on/off
   */
  async toggleMonitoring() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/settings/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to toggle EDR monitoring`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling EDR monitoring:', error);
      throw error;
    }
  },

  /**
   * Enable EDR monitoring
   */
  async enableMonitoring() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/settings/enable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to enable EDR monitoring`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error enabling EDR monitoring:', error);
      throw error;
    }
  },

  /**
   * Disable EDR monitoring
   */
  async disableMonitoring() {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await authenticatedFetch(`${API_URL}/api/edr/settings/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to disable EDR monitoring`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error disabling EDR monitoring:', error);
      throw error;
    }
  }
};

export default edrService;
